import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/** Same surface as filter pills / TattooSearchBar side controls (unselected `Button` secondary). */
const filterInactiveSurface =
  'bg-secondary text-secondary-foreground hover:bg-secondary/80 m-0 p-0 border-0 shrink-0'

export interface ChatsSearchBarProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export function ChatsSearchBar({ value, onChange, placeholder = 'Search messages...' }: ChatsSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDarkMode, setIsDarkMode] = useState(false)

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'))
    }
    checkDarkMode()
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const iconColor = isDarkMode ? '#ffffff' : '#000000'
  const showClear = Boolean(value.trim())

  return (
    <div>
      <div className="relative flex items-center bg-background border border-border rounded-lg shadow-sm">
        <Input
          ref={inputRef}
          type="text"
          enterKeyHint="search"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 rounded-l-lg rounded-r-none border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-4 py-3"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        {showClear ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('h-12 w-12 rounded-r-lg', filterInactiveSurface)}
            onClick={() => onChange('')}
            aria-label="Clear search"
          >
            <X className="h-4 w-4" style={{ color: iconColor }} />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('h-12 w-12 rounded-r-lg', filterInactiveSurface)}
            onClick={() => inputRef.current?.focus()}
            aria-label="Search"
          >
            <Search className="h-4 w-4" style={{ color: iconColor }} />
          </Button>
        )}
      </div>
    </div>
  )
}
